import { Dialog } from 'components/Dialog/Dialog'
import { styled, useTheme } from 'styled-components'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useBackendClient } from '@/shared/kai/contexts/BackendClientContext'
import { useDAppKit } from '@mysten/dapp-kit-react'
import { useEffect, useState } from 'react'
import { Typography } from 'components/Typography/Typography'
import { IconClose } from 'components/Icons/IconClose'
import { Button } from 'components/Button/Button'
import { Spinner } from 'components/Spinner/Spinner'
import {
  EXPIRATION_CODE_SEGMENT_COUNT,
  EXPIRATION_CODE_SEGMENT_LENGTH,
  EXPIRATION_CODE_VALID_CHARACTERS,
} from '@/shared/constants'
import { telegramConnectionQueryKey, useIsTelegramConnected } from 'utils/useIsTelegramConnected'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const TELEGRAM_BOT_NAME = 'KaiFinanceBot'
const TELEGRAM_BOT_URL = `https://t.me/${TELEGRAM_BOT_NAME}?start`

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0px 24px;
  border-radius: 100px;
  border: 1px solid ${p => p.theme.colors.text.primary};
  width: calc(100% - 48px);
  margin-top: 10px;
  background-color: ${p => p.theme.colors.background.primary};
`

const StyledInput = styled.input`
  border: none;
  outline: none;
  flex-grow: 1;
  font-size: 18px;
  background-color: transparent;
  color: ${p => p.theme.colors.text.primary};
  &::placeholder {
    color: ${p => p.theme.colors.input.placeholder};
  }
`

const VerificationInputContainer = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  flex-direction: column;
`

export const ButtonWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
`

export const StyledTypographyWrapper = styled.div`
  margin: 10px;
`

const showErrorToast = (message: string) => {
  toast.error(<div style={{ display: 'flex', alignItems: 'center' }}>{message}</div>)
}

const showSuccessToast = (message: string) => {
  toast.success(<div style={{ display: 'flex', alignItems: 'center' }}>{message}</div>)
}

export const AlertsDialog = ({ isOpen, onClose }: Props) => {
  const backendClient = useBackendClient()
  const dAppKit = useDAppKit()
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showVerificationInput, setShowVerificationInput] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const queryClient = useQueryClient()
  const walletAddress = useCurrentWalletAddress()

  const connectionStatus = useIsTelegramConnected()

  useEffect(() => {
    if (!isOpen) {
      setIsConnected(false)
      setShowVerificationInput(false)
      setVerificationCode('')
      setIsLoading(false)
      return
    }
    setIsConnected(connectionStatus?.isConnected ?? false)
  }, [connectionStatus, isOpen])

  const handleConnect = () => {
    window.open(TELEGRAM_BOT_URL, '_blank')
    setIsLoading(false)
    setShowVerificationInput(true)
  }

  const verifyMutation = useMutation({
    mutationFn: async ({ signature, message }: { signature: string; message: string }) => {
      if (!walletAddress) throw new Error('Wallet not connected')
      return backendClient.verifyTelegram(walletAddress, message, signature)
    },
    onSuccess: data => {
      if (data.isConnected) {
        setIsConnected(true)
        setShowVerificationInput(false)
        queryClient.setQueryData(telegramConnectionQueryKey(walletAddress), { isConnected: true })
        showSuccessToast('Telegram connected successfully')
        onClose()
      } else {
        showErrorToast('Verification failed')
        throw new Error('Verification failed')
      }
    },
    onError: error => {
      showErrorToast(error instanceof Error ? error.message : 'Verification failed')
    },
    onSettled: () => {
      setIsLoading(false)
    },
    retry: false,
  })

  const handleVerify = async () => {
    if (!walletAddress || verificationCode.trim().length === 0) {
      return
    }

    setIsLoading(true)

    const message = `${verificationCode.trim()}:verify:${Date.now()}`

    try {
      const { signature } = await dAppKit.signPersonalMessage({
        message: new TextEncoder().encode(message),
      })
      verifyMutation.mutate({ signature, message })
    } catch (error) {
      if (error instanceof Error) {
        showErrorToast(error.message)
      }
      setIsLoading(false)
    }
  }

  const validateVerificationCode = (code: string): boolean => {
    const segments = code.split('-')
    const isValidFormat =
      segments.length === EXPIRATION_CODE_SEGMENT_COUNT &&
      segments.every(
        segment =>
          segment.length === EXPIRATION_CODE_SEGMENT_LENGTH &&
          segment.split('').every(char => EXPIRATION_CODE_VALID_CHARACTERS.includes(char))
      )

    return isValidFormat
  }

  const disconnectMutation = useMutation({
    mutationFn: async ({ signature, message }: { signature: string; message: string }) => {
      if (!walletAddress) throw new Error('Wallet not connected')
      return backendClient.disconnectTelegram(walletAddress, message, signature)
    },
    onSuccess: data => {
      if (!data.isConnected) {
        queryClient.setQueryData(telegramConnectionQueryKey(walletAddress), { isConnected: false })
        setIsConnected(false)
        setShowVerificationInput(false)
        setVerificationCode('')
        showSuccessToast('Telegram disconnected successfully')
        onClose()
      } else {
        showErrorToast('Disconnect failed')
        throw new Error('Disconnect failed')
      }
    },
    onError: error => {
      showErrorToast(error instanceof Error ? error.message : 'Failed to disconnect Telegram')
      setIsConnected(true)
    },
    onSettled: () => {
      setIsLoading(false)
    },
    retry: false,
  })

  const handleDisconnect = async () => {
    if (!walletAddress) {
      return
    }

    setIsLoading(true)

    const message = `disconnect:${Date.now()}`

    try {
      const { signature } = await dAppKit.signPersonalMessage({
        message: new TextEncoder().encode(message),
      })
      disconnectMutation.mutate({ signature, message })
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        showErrorToast(error.message)
      }
      setIsLoading(false)
      setIsConnected(true)
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      void handleVerify()
    }
  }

  const renderConnectionStatus = () => {
    if (isConnected) {
      return (
        <Typography variant="description" fontWeight="light" style={{ textAlign: 'center' }}>
          You are <strong>connected</strong> to the Telegram bot and will receive notifications.
        </Typography>
      )
    } else if (!showVerificationInput) {
      return (
        <Typography variant="description" fontWeight="light" style={{ textAlign: 'center' }}>
          <Typography variant="description" fontWeight="light" style={{ marginBottom: '10px' }}>
            Connect to our Telegram bot to receive alerts and notifications directly in Telegram.
          </Typography>
          <Typography variant="description" fontWeight="light">
            Once you open a link, press <strong>/start</strong>
          </Typography>
        </Typography>
      )
    }
    return null
  }

  const renderVerificationInput = () => {
    if (showVerificationInput) {
      return (
        <VerificationInputContainer>
          <Typography variant="description" fontWeight="light" style={{ textAlign: 'center' }}>
            <Typography variant="description" fontWeight="light" style={{ marginBottom: '10px' }}>
              Click <strong>/start</strong> in Telegram to begin.
            </Typography>
            <Typography variant="description" fontWeight="light">
              Insert your verification code from <strong>Telegram</strong> to get alert notifications.
            </Typography>
          </Typography>
          <SearchInputContainer>
            <StyledInput
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              placeholder="Enter your verification code"
              onKeyDown={handleKeyPress}
            />
            {verificationCode.length > 0 && (
              <div style={{ display: 'flex', cursor: 'pointer' }} onClick={() => setVerificationCode('')}>
                <IconClose />
              </div>
            )}
          </SearchInputContainer>
        </VerificationInputContainer>
      )
    }
    return null
  }

  const theme = useTheme()

  return (
    <Dialog title="Alerts" isOpen={isOpen} onClose={onClose}>
      <div className="telegram-login">
        <StyledTypographyWrapper>
          <Typography
            variant="description"
            fontWeight="regular"
            color="primary"
            fontFamily="manrope"
            style={{ textAlign: 'center' }}
          >
            {renderConnectionStatus()}
          </Typography>
        </StyledTypographyWrapper>
        {renderVerificationInput()}
        <ButtonWrapper>
          <Button
            onClick={isConnected ? handleDisconnect : showVerificationInput ? handleVerify : handleConnect}
            className="connect-button"
            disabled={isLoading || (showVerificationInput && !validateVerificationCode(verificationCode))}
            outlined={isConnected}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 49,
            }}
          >
            {isLoading ? (
              isConnected ? (
                <Spinner color={theme.colors.text.primary} />
              ) : (
                <Spinner />
              )
            ) : isConnected ? (
              'Disconnect Telegram'
            ) : showVerificationInput ? (
              'Verify Telegram'
            ) : (
              'Connect Telegram'
            )}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  )
}
